import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  EditOutline24Icon,
  EditOutline30Icon,
} from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { queryClient } from "api/tanStackQuery/queryClient";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { PanelAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import { truthy } from "features/CenterContent/VehicleDetails/shared/utils";
import { FC, useEffect, useState } from "react";
import { ActionModal } from "shared/components/ActionModal";
import { StaticInfoMessage } from "shared/components/feedback/InfoMessage/StaticInfoMessage";
import { RadioButton } from "shared/components/forms/RadioButton";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import { Select } from "shared/components/forms/Select";
import {
  Button,
  Input,
  RadioGroup,
  Stack,
  Switch,
} from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { useFormVerbose } from "shared/form/useFormVerbose";
import {
  operatorNameUpdateRequestSchema,
  PhoneNumberUpdateRequest,
  phoneNumberUpdateRequestSchema,
  Supplier,
  UpdateMultipleAlternativeTransportRequest,
  UpdateOperatorNameUpdateRequest,
  WorkShiftPlan,
} from "shared/types/alternativeTransport";
import styled from "styled-components";

type RadioOptions = "SINGLE_TRIP" | "WHOLE_SHIFT";

const RadioGroupStyled = styled(RadioGroup)`
  width: 100%;
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
  align-content: end;
`;

/**
 * Tillater nummere med eller uten landskoder (f.eks. +47 / 0047 / 47) på lengde {min,max}
 * Eksempler:
 *  +4712345678
 *  004712345678
 *  4712345678
 *  12345678
 *
 * {min,max} verdier hentet fra hentet fra https://worldpopulationreview.com/country-rankings/phone-number-length-by-country
 */
const phoneRegexes: RegExp[] = [
  /^((\+|00)?48)?\d{6,9}$/,
  /^((\+|00)?47)?\d{5,8}$/,
  /^((\+|00)?46)?\d{7,13}$/,
  /^((\+|00)?45)?\d{8}$/,
];

export const isValidPhoneNumber = (phoneNumber: string) =>
  phoneRegexes.some((regex) => regex.test(phoneNumber));

type Props = {
  id: string;
  phoneNumberUpdate?: string | null;
  workShift?: WorkShiftPlan | null;
  operatorUpdate?: {
    operatorName: string | null;
    operatorId: string | null;
  };
};

/**
 * Phone-number edit action descriptor. Always available.
 */
export const useVehiclePhoneNumberAction = ({
  id,
  workShift,
  phoneNumberUpdate,
  operatorUpdate,
}: Props): PanelAction => {
  const [isManualOperatorSwitchChecked, setIsManualOperatorSwitchChecked] =
    useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRadio, setSelectedRadio] =
    useState<RadioOptions>("SINGLE_TRIP");
  const hasPhoneNumber = phoneNumberUpdate !== null && phoneNumberUpdate !== "";
  const currentTrip = workShift?.workShiftTrips.find(
    (trip) => trip.vehicleUuid === id,
  );
  const firstEditTrip = currentTrip ?? workShift?.workShiftTrips.at(0);
  const selectedWorkShiftTrips =
    selectedRadio === "SINGLE_TRIP"
      ? [currentTrip].filter(truthy)
      : workShift?.workShiftTrips.filter(
          (trip) =>
            trip.scheduledDepartureTimeOrigin &&
            firstEditTrip?.scheduledDepartureTimeOrigin &&
            trip.scheduledDepartureTimeOrigin >=
              firstEditTrip?.scheduledDepartureTimeOrigin,
        );
  const selectedVehicleIds =
    selectedWorkShiftTrips?.map((trip) => trip.vehicleUuid) ?? [];
  const selectedDistributionStopReferences =
    selectedWorkShiftTrips
      ?.flatMap((trip) => trip.stopReferences)
      .filter(truthy) ?? [];

  const combinedSchema = phoneNumberUpdateRequestSchema.merge(
    operatorNameUpdateRequestSchema,
  );
  const {
    formState: { errors: formErrors, isSubmitting },
    watch,
    reset: resetForm,
    handleSubmit,
    setValue,
  } = useFormVerbose<
    PhoneNumberUpdateRequest & UpdateOperatorNameUpdateRequest
  >({
    resolver: zodResolver(combinedSchema),
    defaultValues: {
      phoneNumber: phoneNumberUpdate ?? "",
      distributionStopReferences: selectedDistributionStopReferences,
      operatorName: operatorUpdate?.operatorName ?? "",
      operatorId: operatorUpdate?.operatorId ?? null,
    },
  });

  const {
    data: operators,
    isError: isOperatorsError,
    isLoading: isOperatorsLoading,
  } = useQuery<Supplier[]>({
    queryKey: ["operators"],
    queryFn: ({ signal }) =>
      queryFnGET<Supplier[]>({
        signal,
        url: `${getBackendUrl()}/alternativeTransports/operators`,
      }),
    staleTime: 0,
  });

  const operatorsList = operators ?? [];
  const phoneNumber = watch("phoneNumber");
  const operatorName = watch("operatorName");
  const operatorId = watch("operatorId");
  const {
    mutate: patchRequest,
    status: patchRequestStatus,
    reset: resetStatus,
  } = useMutation({
    mutationFn: async (data: UpdateMultipleAlternativeTransportRequest) => {
      const response = await axiosClient.patch<any>(
        `${getBackendUrl()}/alternativeTransports`,
        data,
      );
      return response.data;
    },
    onSuccess: async () => {
      await new Promise((resolve) => {
        setTimeout(resolve, 2000);
      });

      [
        ["altTransDetails", id],
        ["alternativeTransports", id],
        ["reserveAltTransport"],
        ["backupDetails"],
        ["altTransport"],
        ["altTransportMultiday"],
      ].forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));

      setShowModal(false);
      resetForm();
      resetStatus();
    },
  });

  const onSubmitForm = handleSubmit((data) => {
    /**
     * Converts "" to null
     * We use null to delete the phoneNumber
     */

    const payload: UpdateMultipleAlternativeTransportRequest = {
      uuids: selectedRadio === "SINGLE_TRIP" ? [id] : selectedVehicleIds,
      updateRequest: {
        phoneNumberUpdateRequest: {
          phoneNumber: data.phoneNumber,
          distributionStopReferences: data.distributionStopReferences,
        },
        operatorNameUpdateRequest: {
          operatorId: data.operatorId === "" ? null : data.operatorId,
          operatorName: data.operatorName,
          distributionStopReferences: data.distributionStopReferences,
        },
      },
    };
    patchRequest(payload);
  });

  useEffect(() => {
    if (showModal)
      resetForm({
        phoneNumber: phoneNumberUpdate ?? "",
        distributionStopReferences: selectedDistributionStopReferences,
        operatorName: operatorUpdate?.operatorName ?? "",
        operatorId: operatorUpdate?.operatorId ?? "",
      });
  }, [showModal]);

  const onClose = () => {
    setShowModal(false);
    resetForm();
    resetStatus();
  };

  const isValidOperator = operatorsList?.some(
    (operator) =>
      operator.id.toString() === operatorUpdate?.operatorId ||
      operator.name === operatorUpdate?.operatorName,
  );

  /*
    This is a workaround. If we send in the plus to Puzzel, it is stripped away
    before the 18740 prefix is added. This works fine if you're an admin, but
    not for normal DROPS users. But if we replace the + with 00, it is not stripped,
    and with the 00 it works just fine. Don't ask me why.
   
    These formats work for everyone:
    18740 0047 12345678 <- Result from what we send in with the workaround
    18740 12345678
    0047 12345678
    12345678

    This format only works for admins:
    18740 47 12345678 <- Result from what we send in without the workaround
  */

  return {
    key: "phone",
    label: "Rediger",
    leftIcon: <EditOutline24Icon />,
    buttonVariant: "tertiary",
    buttonSize: "xs",
    onSelect: () => setShowModal(true),
    modal: showModal ? (
      <ActionModal
        title="Rediger buss"
        actionTitle={hasPhoneNumber ? "Fullfør" : "Opprett"}
        onSubmit={onSubmitForm}
        onClose={onClose}
        loading={patchRequestStatus === "pending"}
        isSuccess={patchRequestStatus === "success"}
        isError={patchRequestStatus === "error"}
        successMessage={
          hasPhoneNumber
            ? "Endringen av telefonnummer var vellykket."
            : "Opprettelsen av telefonnummer var vellykket."
        }
        failureMessage={`Kunne ikke ${
          hasPhoneNumber ? "endre" : "opprette"
        } telefonnummer. Prøv igjen, eller kontakt IT hvis feilen vedvarer.`}
      >
        <Stack gap={2} width="100%">
          {isOperatorsError && (
            <StaticInfoMessage
              semantics="alarm"
              summary="Kunne ikke laste inn selskapsliste. Prøv å lukke og åpne vinduet igjen, eller kontakt IT hvis feilen vedvarer."
            />
          )}

          {!isValidOperator && operatorUpdate?.operatorName && (
            <StaticInfoMessage
              semantics="warning"
              summary={`Selskapet "${operatorUpdate.operatorName}" finnes ikke i listen. Velg selskapet fra listen hvis du finner det. Hvis ikke kan du endre operatørnavnet manuelt.`}
            />
          )}

          <Switch
            label="Skriv selskapsnavn manuelt"
            size="sm"
            checked={isManualOperatorSwitchChecked}
            onChange={() =>
              setIsManualOperatorSwitchChecked(!isManualOperatorSwitchChecked)
            }
          />

          {isManualOperatorSwitchChecked ? (
            <Input
              label="Skriv inn selskapsnavn"
              value={operatorName ?? ""}
              onChange={(event) => {
                setValue("operatorName", event.target.value);
                setValue("operatorId", "");
              }}
              disabled={isSubmitting}
              aria-labelledby="modal-heading-title"
              aria-invalid={formErrors.operatorName !== undefined}
              aria-errormessage="operatorName-error-message"
            />
          ) : (
            <Select
              label="Velg selskapsnavn"
              value={operatorId ?? ""}
              disabled={isOperatorsLoading || isOperatorsError}
              onChange={(event) => {
                const selectedOperator = operatorsList?.find(
                  (operator) => operator.id.toString() === event.target.value,
                );

                setValue("operatorName", selectedOperator?.name ?? "");
                setValue("operatorId", selectedOperator?.id.toString() ?? "");
              }}
            >
              <option value="">-</option>
              {operators?.map((operator) => (
                <option key={operator.id} value={operator.id.toString()}>
                  {operator.name}
                </option>
              ))}
            </Select>
          )}
          <Input
            label="Skriv inn telefonnummer"
            value={phoneNumber ?? ""}
            onChange={(event) => setValue("phoneNumber", event.target.value)}
            disabled={isSubmitting}
            aria-labelledby="modal-heading-title"
            aria-invalid={formErrors.phoneNumber !== undefined}
            aria-errormessage="phoneNumber-error-message"
          />

          <RenderErrorInPath errors={formErrors} errorPath="phoneNumber" />
        </Stack>
        <RadioGroupStyled
          role="radiogroup"
          defaultValue="SINGLE_TRIP"
          style={{ paddingTop: "12px" }}
        >
          <RadioButton
            value="SINGLE_TRIP"
            onChange={() => setSelectedRadio("SINGLE_TRIP")}
          >
            Bare denne turen
          </RadioButton>
          {workShift && (
            <RadioButton
              value="WHOLE_SHIFT"
              onChange={() => setSelectedRadio("WHOLE_SHIFT")}
            >
              {currentTrip ? "Denne turen og ut skiftet" : "Hele skiftet"}
            </RadioButton>
          )}
        </RadioGroupStyled>
        {selectedRadio === "WHOLE_SHIFT" && (
          <>
            <Text style={{ textAlign: "center" }}>
              Dette vil endre telefonnummer til {phoneNumberUpdate} på{" "}
              {currentTrip ? "denne og resterende" : "alle"} turer tilhørende
              skift {workShift?.shiftId}.
            </Text>
            <Text>Er du sikker på at du vil gå videre?</Text>
          </>
        )}
      </ActionModal>
    ) : null,
  };
};

export const VehiclePhoneNumber: FC<Props> = ({
  id,
  workShift,
  phoneNumberUpdate,
  operatorUpdate,
}) => {
  const action = useVehiclePhoneNumberAction({
    id,
    workShift,
    phoneNumberUpdate,
    operatorUpdate,
  });

  return (
    <>
      <Button
        variant="tertiary"
        size="xs"
        onClick={action.onSelect}
        leftIcon={<EditOutline30Icon />}
      >
        <Text>Rediger</Text>
      </Button>
      {action.modal}
    </>
  );
};
